import { useEffect } from 'react'

import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import { useModal } from '@/contexts/modal'

import { galleryViewerRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type GalleryViewerProps = {
  overline: string
  title: string
  gallery: WPImage[]
}

const GalleryViewer = ({ overline, title, gallery }: GalleryViewerProps) => {
  const { openModalGallery, createModalGallery } = useModal()

  useEffect(() => createModalGallery(gallery), [gallery, createModalGallery])

  return (
    <Motion animation={galleryViewerRevealAnimation}>
      {(motionRef) => (
        <Section
          ref={motionRef}
          className="mb-spacing-lg lg:-mr-[4rem] lg:mb-0"
        >
          <Grid>
            <div
              className={cn([
                'col-span-2 md:col-span-4 md:col-start-2',
                'lg:col-auto lg:row-span-7 lg:row-start-3',
                'grid items-end'
              ])}
            >
              <button
                onClick={() => openModalGallery()}
                className={cn(['group block rounded-[0.8rem] outline-none'])}
              >
                <span
                  className={cn([
                    'flex rounded-[0.8rem] border border-dashed border-text-handwritten-light p-spacing-lg pb-spacing-md',
                    'transition-transform duration-500 ease-out-cubic will-change-transform group-hover:scale-90'
                  ])}
                >
                  <span
                    className={cn([
                      'mx-auto flex w-full flex-col items-center space-y-spacing-lg rounded-[0.8rem] outline-none md:w-auto md:flex-row md:space-x-spacing-lg md:space-y-spacing-none',
                      'transition-transform duration-500 ease-out-cubic will-change-transform group-hover:scale-110'
                    ])}
                  >
                    <span className="flex flex-col items-center md:items-start">
                      <span className="flex">
                        <Typography
                          variant="h6"
                          paragraph
                          className="text-text-handwritten-light"
                          data-motion-overline
                        >
                          {overline}
                        </Typography>
                      </span>
                      <span className="mt-spacing-2xs flex">
                        <Typography variant="h2" paragraph data-motion-title>
                          {title}
                        </Typography>
                      </span>
                    </span>
                    <span
                      className="flex h-[8rem] w-[8rem] rounded-full"
                      data-motion-btn-wrapper
                    >
                      <span
                        className="flex h-full w-full rounded-full"
                        data-motion-btn-content
                      >
                        <span
                          className={cn([
                            'flex h-full w-full items-center justify-center rounded-full bg-surface-primary-dark',
                            'transition-transform duration-500 ease-out-cubic will-change-transform group-hover:scale-90'
                          ])}
                        >
                          <span
                            className={cn([
                              'relative block h-[2.2rem] w-[1.7rem] overflow-hidden rounded-full',
                              'transition-transform duration-500 ease-out-cubic will-change-transform group-hover:scale-110'
                            ])}
                          >
                            <SVGIcon
                              symbol="lens-icon"
                              className={cn([
                                'h-[2.2rem] w-[1.7rem] fill-surface-primay-invert-light',
                                'transition-transform duration-500 ease-out-cubic will-change-transform group-hover:-translate-y-full'
                              ])}
                            />
                            <SVGIcon
                              symbol="lens-icon"
                              className={cn([
                                'h-[2.2rem] w-[1.7rem] fill-surface-primay-invert-light',
                                'transition-transform duration-500 ease-out-cubic will-change-transform group-hover:-translate-y-full'
                              ])}
                            />
                          </span>
                        </span>
                      </span>
                    </span>
                  </span>
                </span>
              </button>
            </div>
          </Grid>
        </Section>
      )}
    </Motion>
  )
}

export default GalleryViewer
